import { defineStore } from 'pinia';  
import { ref } from 'vue';
import type GuestInfo from '../types/GuestInfo'
import type Basket from '../classes/Basket';
import BasketProduct from '../classes/BasketProduct';
import type Order from '../classes/Order';
import type OrderDetails from '../classes/OrderDetails';
import BasketService from '../services/Basket';
import CateringOrdersService from '../services/CateringOrders';
import OrdersService from '../services/Orders';
import { useUserStore } from './user';
import { useMenuStore } from './menu';
import { useFlashMessageStore } from './flashMessages';
import type FlashMessage from '../types/FlashMessage';
import type LoyaltyScheme from '../classes/LoyaltyScheme';
import type Location from '../classes/Location';
import type AvailableTime from '../types/AvailableTime';
import type BillingScheme from '../types/BillingScheme';
import type Product from '../classes/Product';
import type Reward from '../types/Reward';
import type DerivedDiscount from '../types/DerivedDiscount';
import type LoyaltyReward from '../types/LoyaltyReward';
import type AppliedReward from '../types/AppliedReward';
import type RewardInterface from '../types/Reward';
import type CateringOrder from '../types/CateringOrder';
import type OrderProduct from '../types/OrderProduct';
import { useLocationsStore } from './locations';
import { useCateringOrderStore } from './catering_order';
import { plainToClass } from 'class-transformer';
import { DateTime } from 'luxon';
import type ChoiceCustomFieldInterface from '@/types/ChoiceCustomField';

const enum BasketMutationPriority {
  High = 0,
  Medium = 1,
  Low = 2,
}

type MutationRunner<T> = () => Promise<T>;

type MutationTask = {
  runner: () => Promise<unknown>;
  resolve: (value: unknown) => void;
  reject: (reason: unknown) => void;
};

// Priority guide:
// - High: core basket mutations (create/clear, add/remove/update products, reorder/edit flows).
// - Medium: ancillary adjustments (fetch basket, delivery/tip, time wanted, upsells, orders lookups).
// - Low: long-running validations/submissions/promos/rewards to ensure they run after high/medium tasks.

const mutationBuckets: Record<BasketMutationPriority, MutationTask[]> = {
  [BasketMutationPriority.High]: [],
  [BasketMutationPriority.Medium]: [],
  [BasketMutationPriority.Low]: [],
};

let mutationActive = false;
const mutationQueueDepth = ref(0);

const decrementPendingMutations = () => {
  if (mutationQueueDepth.value > 0) {
    mutationQueueDepth.value -= 1;
  } else {
    mutationQueueDepth.value = 0;
  }
};

function shiftNextMutationTask(): MutationTask | undefined {
  if (mutationBuckets[BasketMutationPriority.High].length > 0) {
    return mutationBuckets[BasketMutationPriority.High].shift();
  }

  if (mutationBuckets[BasketMutationPriority.Medium].length > 0) {
    return mutationBuckets[BasketMutationPriority.Medium].shift();
  }

  if (mutationBuckets[BasketMutationPriority.Low].length > 0) {
    return mutationBuckets[BasketMutationPriority.Low].shift();
  }

  return undefined;
}

async function processMutationQueue(): Promise<void> {
  if (mutationActive) {
    return;
  }

  mutationActive = true;

  let task = shiftNextMutationTask();
  while (task) {
    try {
      const result = await task.runner();
      task.resolve(result);
    } catch (error) {
      task.reject(error);
    } finally {
      decrementPendingMutations();
    }

    task = shiftNextMutationTask();
  }

  mutationActive = false;
}

function enqueueMutation<T>(priority: BasketMutationPriority, runner: MutationRunner<T>): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const task: MutationTask = {
      runner: () => runner(),
      resolve: (value: unknown) => resolve(value as T),
      reject,
    };
    mutationBuckets[priority].push(task);
    mutationQueueDepth.value += 1;
    processMutationQueue();
  });
}

export const useBasketStore = defineStore({
  id: "basketStore",
  state: () => ({
    order: null as (OrderDetails | null),
    basket: null as (Basket | null),
    sessionBasketId: null as (string | null),
    editing: false,
    editOrderCutoffTime: '',
    hasAcknowledgedEditing: false,
  }),
  getters: {
    itemCount(state): number {
      // Loop over products in the basket and get their quantity
      if (this.basket && this.basket.products) {
        let count = 0;
        this.basket.products.forEach((product) => {
          count += product.quantity;
        })

        return count;
      } else {
        return 0
      }
    },
    currentDiscounts(state): DerivedDiscount[] {
      if (this.basket && this.basket.deriveddiscounts) {
        return this.basket.deriveddiscounts;
      } else {
        return [] as DerivedDiscount[];
      }
    },
    mutationQueueDepth(): number {
      return mutationQueueDepth.value;
    },
    hasPendingMutations(): boolean {
      return mutationQueueDepth.value > 0;
    },
  },
  actions: {
    mergeValidationBasket(validatedBasket: Basket) {
      if (this.basket && validatedBasket) {
         this.basket.salestax = validatedBasket.tax
         this.basket.deriveddiscounts = validatedBasket.deriveddiscounts;
         this.basket.subtotal = validatedBasket.subtotal
         this.basket.total = validatedBasket.total
         this.basket.totalfees = validatedBasket.totalfees
      }
    },
    processBasketProducts(basketProducts: BasketProduct[], basketLocation: number): Promise<any> {
      // Takes an array of basketProducts, sends back an updated array of basketProducts
      const menuStore = useMenuStore();
      const locationStore = useLocationsStore();  

      return new Promise((resolve) => {
        if (!basketProducts || basketProducts.length === 0) {
          resolve([]);
          return;
        }

        const normalizeProducts = (productLookup?: Map<string, Product>) => {
          const normalized = [] as BasketProduct[];

          basketProducts.forEach((bp) => {
            const prod = plainToClass(BasketProduct, (bp as object), { excludeExtraneousValues: true });
            const prodId = (typeof(prod.productId) != undefined) ? prod.productId : prod.id;
            const match = prodId && productLookup ? productLookup.get(prodId.toString()) : undefined;

            if (match) {
              prod.metadata = match.metadata ?? [];
              prod.images = match.images ?? [];
              prod.chainproductid = match.chainproductid ? Number(match.chainproductid) : prod.chainproductid;
              prod.feeds = match.feeds ?? [0, 0, 0];
              prod.custom_data = match.custom_data;
              prod.description = match.description ?? "";
            } else {
              prod.metadata = [];
              prod.images = [];
              prod.feeds = [0, 0, 0];
              prod.description = "";
            }

            normalized.push(prod);
          });

          return normalized;
        };

        const fallbackProducts = normalizeProducts();

        if (!basketLocation) {
          resolve(fallbackProducts);
          return;
        }

        locationStore.getLocationById(basketLocation).then((loc: Location) => {
          if (!loc || !loc.extref) {
            resolve(fallbackProducts);
            return;
          }

          menuStore.getIdKeyedProducts(parseFloat(loc.extref)).then((products) => {
            resolve(normalizeProducts(products));
          }).catch(() => {
            resolve(fallbackProducts);
          });
        }).catch(() => {
          resolve(fallbackProducts);
        });
      })
    },
    createBasket(): Promise<any> {
      const userStore = useUserStore();
      const cateringOrderStore = useCateringOrderStore();

      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        if (userStore.userLocation) {
          BasketService.createBasket(userStore.userLocation!.id).then((basket: Basket) => {
            localStorage.setItem('mypiadaBasketId', basket.id);
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);

            // If we have a cateringOrder already, update it to have the new basket ID
            if (cateringOrderStore.cateringOrder) {
              cateringOrderStore.cateringOrder.basket_id = basket.id;
              cateringOrderStore.updateCateringOrder(cateringOrderStore.cateringOrder.id, cateringOrderStore.cateringOrder);
            }

            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });
          });
        } else {
          // TODO: How do error?
          alert('Error: Location has not been set.');
          reject("No location has been set")
        }
      }));
    }, 
    fetchBasket() {
      const userStore = useUserStore();
      const basketId = localStorage.getItem('mypiadaBasketId');

      if (basketId) {
        BasketService.getBasket(basketId).then((basket: Basket) => {
          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;
            if (this.basket != null) {
              this.basket.sourceOrder().then((order: OrderDetails) => {
                if (order) {
                  this.editing = true;
                  this.editOrderCutoffTime = order.edit_cutoff_time;
                }
              });
            }
          });

          if (this.sessionBasketId != basket.id) {
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);
          }

          localStorage.setItem('mypiadaBasketId', basket.id);
        }).catch((error: Error) => {
          BasketService.setMypiadaBasket(undefined);
          localStorage.removeItem('mypiadaBasketId');
        });
      }
    },
    fetchBasketWithPromise(basketId: string): Promise<any> {
      const userStore = useUserStore()
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        if (basketId) {
          BasketService.getBasket(basketId).then((basket: Basket) => {
            if (this.sessionBasketId != basket.id) {
              BasketService.setMypiadaBasket(basket.id);
            }

            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              if (this.basket != null) {
                  if (this.basket.sourceorderguid != null && this.basket.sourceorderguid != "") {
                  this.basket.sourceOrder().then((order: OrderDetails) => {
                    if (order) {
                      this.editing = true;
                      this.editOrderCutoffTime = order.edit_cutoff_time;
                    }
                    resolve(basket);
                  }).catch((error: Error) => {
                    this.editing = false;
                    this.clearBasket({ skipQueue: true }).then(() => {
                      reject(error)
                    });
                  });
                } else {
                  resolve(basket);
                }
              }
            });

            if (this.sessionBasketId != basket.id) {
              BasketService.setMypiadaBasket(basket.id, basket.vendorid);
            }

            localStorage.setItem('mypiadaBasketId', basket.id);
          }).catch((error: Error) => {
            BasketService.setMypiadaBasket(undefined);
            localStorage.removeItem('mypiadaBasketId');
            reject(error);
          });
        } else {
          reject(new Error('Basket not found'));
        }
      }));
    },
    clearBasket(options?: { skipQueue?: boolean }): Promise<any> {
      const runClear = () => new Promise((resolve, reject) => {
        BasketService.setMypiadaBasket(undefined).then(() => {
          this.basket = null as (Basket | null);
          localStorage.removeItem('mypiadaBasketId');
          this.editing = false;
          this.editOrderCutoffTime = "";
          resolve('');
        }).catch((error: Error) => {
          reject(error)
        })
      });

      if (options?.skipQueue) {
        return runClear();
      }

      return enqueueMutation(BasketMutationPriority.High, runClear);
    },
    //--------------------------------------------------------------------------------------
    //
    // Generic Olo Orders
    //
    //--------------------------------------------------------------------------------------

    getOrder(id: string): Promise<any> {
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        OrdersService.getOrder(id).then((order: OrderDetails) => {
          this.processBasketProducts(order.products, order.vendorid).then((processedBasketProducts) => {
            this.basket = null; // Clear out the basket
            order.products = processedBasketProducts;
            this.order = order;
            resolve(this.order);
          });
        });
      }));
    },
    cancelOrder(id?: string):Promise<any> {
      const userStore = useUserStore();
      
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        let toCancelId = id
        if (!toCancelId && this.order) {
          toCancelId = this.order.id;
        }
        if (toCancelId) {
          OrdersService.cancelOrder(toCancelId).then((order: OrderDetails) => {
            // Should return the order details but now status should be set to cancelled?
            this.processBasketProducts(order.products, order.vendorid).then((processedBasketProducts) => {
              this.basket = null; // Clear out the basket
              order.products = processedBasketProducts;
              this.order = order;

              localStorage.removeItem('orderId'); // clean up

              userStore.fetchRecentOrders(4);

              resolve(this.order);
            });
          }).catch((error: Error) => {
            reject(error)
          });
        }
      }));
    },

    //--------------------------------------------------------------------------------------
    //
    // Generic Olo Orders
    //
    //--------------------------------------------------------------------------------------

    reorder(id: string): Promise<any> {
      const userStore = useUserStore();

      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        const cateringOrderStore = useCateringOrderStore();

        let previousCateringOrder: CateringOrder | null = null;

        if (cateringOrderStore.cateringOrder) {
          previousCateringOrder = JSON.parse(JSON.stringify(cateringOrderStore.cateringOrder));
        }

        BasketService.reorder(id).then((basket: Basket) => {
          const locationsStore = useLocationsStore();
          const userStore = useUserStore();
          
          if (locationsStore.locations) {
            const locationObj = locationsStore.locations.find((loc: Location) => {
              return loc.id == basket.vendorid
            })

            if (locationObj) {            
              userStore.userLocation = locationObj
            } else {
              // Not really sure what to do here, shouldn't happen unless the store closes or 
              // User has demo vendor orders
            }
          } else {
            // We're going to have to fetch locations first
          }

          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;
          });

          if (this.sessionBasketId != basket.id) {
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);
          }

          userStore.fetchRecentOrders(4);

          if (basket.catering_order != null && basket.catering_order.id != null) {
            const cateringOrder: CateringOrder = basket.catering_order;
            const cateringOrderStore = useCateringOrderStore();
            localStorage.setItem('cateringOrder', cateringOrder.id.toString());
            cateringOrderStore.cateringOrder = cateringOrder;

            cateringOrderStore.suggestions = null; // The order was created, so make sure we can get updated suggestions

            if (previousCateringOrder != null && previousCateringOrder.store == cateringOrderStore.cateringOrder.store && previousCateringOrder.time_wanted != null) {
              cateringOrderStore.cateringOrder.time_wanted = previousCateringOrder.time_wanted!;
            }

            cateringOrderStore.updateCateringOrder(cateringOrderStore.cateringOrder.id, cateringOrderStore.cateringOrder).then((cateringOrder) => {
              // Wait for the catering order to update first
              localStorage.setItem('mypiadaBasketId', basket.id);

              resolve(basket);
            }).catch((error: Error) => {
              // Just resolvethe basket
              localStorage.setItem('mypiadaBasketId', basket.id);

              resolve(basket);
            })
          } else {
            localStorage.setItem('mypiadaBasketId', basket.id);

            resolve(basket);
          }

        }).catch((error: Error) => {
          BasketService.setMypiadaBasket(undefined);
          localStorage.removeItem('mypiadaBasketId');

          reject(error)
        });
      }));
    },

    editOrder(id: string): Promise<any> {
      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        const cateringOrderStore = useCateringOrderStore();
        let previousCateringOrder: CateringOrder | null = null;

        if (cateringOrderStore.cateringOrder) {
          previousCateringOrder = JSON.parse(JSON.stringify(cateringOrderStore.cateringOrder));
        }

        BasketService.editOrder(id).then((basket: Basket) => {
          const locationsStore = useLocationsStore();
          const userStore = useUserStore();
          
          if (locationsStore.locations) {
            const locationObj = locationsStore.locations.find((loc: Location) => {
              return loc.id == basket.vendorid
            })

            if (locationObj) {            
              userStore.userLocation = locationObj
            } else {
              // Not really sure what to do here, shouldn't happen unless the store closes or 
              // User has demo vendor orders
            }
          } else {
            // We're going to have to fetch locations first
          }

          userStore.fetchRecentOrders(4);

          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;

            if (this.basket != null) {
              this.basket.sourceOrder().then((order: OrderDetails) => {
                if (order) {
                  this.editing = true;
                  this.editOrderCutoffTime = order.edit_cutoff_time;
                }
              }).catch((error: Error) => {
                this.editing = false;
                this.clearBasket({ skipQueue: true }).then(() => {
                  reject(error)
                });
              });
            }
          });

          if (this.sessionBasketId != basket.id) {
            BasketService.setMypiadaBasket(basket.id, basket.vendorid);
          }

          if (basket.catering_order != null && basket.catering_order.id != null) {
            const cateringOrder: CateringOrder = basket.catering_order;
            const cateringOrderStore = useCateringOrderStore();
            localStorage.setItem('cateringOrder', cateringOrder.id.toString());
            cateringOrderStore.cateringOrder = cateringOrder;

            cateringOrderStore.suggestions = null; // The order was created, so make sure we can get updated suggestions

            if (previousCateringOrder != null && previousCateringOrder.store == cateringOrderStore.cateringOrder.store && previousCateringOrder.time_wanted != null) {
              cateringOrderStore.cateringOrder.time_wanted = previousCateringOrder.time_wanted!;
            }

            cateringOrderStore.updateCateringOrder(cateringOrderStore.cateringOrder.id, cateringOrderStore.cateringOrder).then((cateringOrder) => {
              // Wait for the catering order to update first
              localStorage.setItem('mypiadaBasketId', basket.id);

              resolve(basket);
            }).catch((error: Error) => {
              // Just resolvethe basket
              localStorage.setItem('mypiadaBasketId', basket.id);

              resolve(basket);
            })
          } else {
            localStorage.setItem('mypiadaBasketId', basket.id);

            resolve(basket);
          }

        }).catch((error: Error) => {
          BasketService.setMypiadaBasket(undefined);
          localStorage.removeItem('mypiadaBasketId');
          this.editing = false;
          reject(error)
        });
      }));
    },
    cancelEditOrder(): Promise<any> {
      const userStore = useUserStore();
      const cateringOrderStore = useCateringOrderStore();
      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.cancelEditOrder(this.basket.id).then(() => {
            this.basket = null as (Basket | null);
            localStorage.removeItem('mypiadaBasketId');
            localStorage.removeItem('cateringOrder');
            this.editing = false;
            this.editOrderCutoffTime = "";
            cateringOrderStore.clearCateringOrder();
            userStore.fetchRecentOrders(4); // If this fails, it'll fail in the background and we have no recourse
            resolve('');
          }).catch((error: Error) => {
            reject(error)
          })
        }
      }))
    },
    

    //--------------------------------------------------------------------------------------
    //
    // Products
    //
    // -------------------------------------------------------------------------------------

    addProduct(pid: string, qty: number, specialInstructions: string, choices: number[], choicecustomfields: ChoiceCustomFieldInterface[], trackSuggestion?: string, recipient?: string): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();

      if (!this.basket) {
        return this.createBasket().then(() => this.addProduct(pid, qty, specialInstructions, choices, choicecustomfields, trackSuggestion, recipient));
      }

      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        BasketService.addProduct(this.basket!.id, pid, qty, specialInstructions, choices, choicecustomfields, recipient).then((basket: Basket) => {
          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;
            // if (trackSuggestion) {
            //   cateringOrderStore.trackSuggestionConversion(trackSuggestion, pid, qty, specialInstructions, choices, choicecustomfields).then(() => {});
            // }

            resolve(basket);
          });
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },
    addMultipleProducts(products: OrderProduct[], trackSuggestion?: string): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();

      if (!this.basket) {
        return this.createBasket().then(() => this.addMultipleProducts(products, trackSuggestion));
      }

      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        BasketService.addMultipleProducts(this.basket!.id, products).then((basket: Basket) => {
          this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
            basket.products = processedBasketProducts;
            this.basket = basket;
            if (trackSuggestion) {
              products.forEach((prod: OrderProduct) => {
                cateringOrderStore.trackSuggestionConversion(trackSuggestion, prod).then(() => {});
              })
            }

            resolve(basket);
          });
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },
    removeProduct(bpid: number): Promise<any> {
      const userStore = useUserStore();
      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.removeProduct(this.basket.id, bpid).then((basket: Basket) => {
            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });
          });
        } else {
          resolve(null);
        }
      }))
    },
    updateProduct(pid: string, bpid: string, qty: number, specialInstructions: string, choices: number[], choicecustomfields: ChoiceCustomFieldInterface[], recipient?: string): Promise<any> {
      const userStore = useUserStore();
      const flashMessageStore = useFlashMessageStore();

      return enqueueMutation(BasketMutationPriority.High, () => new Promise((resolve, reject) => {
        if (this.basket) {
          BasketService.updateProduct(this.basket.id, pid, bpid, qty, specialInstructions, choices, choicecustomfields, recipient).then((basket: Basket) => {
            const message = {
                id: Math.round(Date.now() / 1000),
                messageType: "success",
                text: "Product has been updated",
                headline: "",
                delay: 5000, // ms
                icon: "",
                cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(basket.products, basket.vendorid).then((processedBasketProducts) => {
              basket.products = processedBasketProducts;
              this.basket = basket;
              resolve(basket);
            });
          }).catch((error: Error) => {
            reject(error)
          });
        } else {
          resolve(null);
        }
      }));
    },
    fetchUpsells(): Promise<any> {
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        BasketService.fetchUpsells(this.basket!.id).then((upsells: Product[]) => {
          resolve(upsells);
        }).catch((error:Error) => {
          reject(error);
        });
      }));
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Information
    //
    // -------------------------------------------------------------------------------------

    getBillingSchemes(): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.getBillingSchemes(this.basket!.id).then((schemes: BillingScheme[]) => {
          resolve(schemes);
        })
      })
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Times
    //
    // -------------------------------------------------------------------------------------

    getAvailableTimes(start: DateTime): Promise<any> {
      return new Promise((resolve, reject) => {
        BasketService.getAvailableTimes(this.basket!.id, start).then((times: AvailableTime[]) => {
          resolve(times);
        })
      })
    },
    setNextAvailableTime(start: DateTime, validate: boolean): Promise<any> {
      const store = this;
      return new Promise((resolve, reject) => {
        // First go grab the next available time
        store.getAvailableTimes(start).then((times: AvailableTime[]) => {
          // Use the next one
          const nextTime = DateTime.fromFormat(times[0].time, 'yyyyMMdd hh:mm');

          store.setTimeWanted(nextTime, validate).then(() => {
            resolve('')
          }).catch((error: Error) => {
            const nextNextTime = DateTime.fromFormat(times[1].time, 'yyyyMMdd hh:mm');
            store.setTimeWanted(nextNextTime, false).then(() => {
              resolve('')
            }).catch((error: Error) => {
              reject(error)
            })
          })
        })
      })
    },
    setTimeWanted(date: DateTime, validate: boolean): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        BasketService.setTimeWanted(this.basket!.id, date).then((localBasket: Basket) => {
          // If the time wanted was updated, and we have a catering order; update it to match
          if (cateringOrderStore.cateringOrder && cateringOrderStore.cateringOrder.time_wanted) {
            let updatedCateringOrder = cateringOrderStore.cateringOrder;
            updatedCateringOrder.time_wanted = date.toISO() as string;
            cateringOrderStore.updateCateringOrder(updatedCateringOrder.id, updatedCateringOrder);
          }

          if (validate) {
            this.validateBasket(false, { skipQueue: true }).then((validatedBasket: Basket) => {
              this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
                localBasket.products = processedBasketProducts;
                this.basket = localBasket;
                this.mergeValidationBasket(validatedBasket);

                resolve(this.basket);
              });
            }).catch((error: any) => {
              // Kick the error back out
              reject(error)
            })
          } else {
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;

              resolve(this.basket);
            });
          }
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Rewards
    //
    // -------------------------------------------------------------------------------------

    applyReward(rewardcode: string): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return enqueueMutation(BasketMutationPriority.Low, () => new Promise((resolve, reject) => {
        BasketService.applyReward(this.basket!.id, rewardcode).then((localBasket: Basket) => {
          this.validateBasket(false, { skipQueue: true }).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Promo has been applied",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);

              resolve(this.basket);
            });
          }).catch((error: Error) => {
            // Validation failed but the promo was still applied so resolve the basket
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;

              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },
    removeReward(reward?: RewardInterface, rewardcode?: string): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return enqueueMutation(BasketMutationPriority.Low, () => new Promise((resolve, reject) => {
        let code = rewardcode;
        if (reward) {
          code = reward.credential_identifier;
        }
        BasketService.removeReward(this.basket!.id, code!).then((localBasket: Basket) => {
          this.validateBasket(false, { skipQueue: true }).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Rewards have been removed",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);

              resolve(this.basket);
            });
            resolve(this.basket);
          }).catch((error: Error) => {
            // Validation failed but the loyalty was still removed so resolve the basket
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      }));          
    },
    // removeRewards(): Promise<any> {
    //   const flashMessageStore = useFlashMessageStore();
    //   return new Promise((resolve, reject) => {
    //     const reward = this.basket!.appliedrewards![0];

    //     BasketService.removeRewards(this.basket!.id, reward.rewardid).then((localBasket: Basket) => {
    //       this.validateBasket(false).then((validatedBasket: Basket) => {
    //         const message = {
    //           id: Math.round(Date.now() / 1000),
    //           messageType: "success",
    //           text: "Rewards have been removed",
    //           headline: "",
    //           delay: 5000, // ms
    //           icon: "",
    //           cssClass: "",
    //         } as FlashMessage;
    //         flashMessageStore.addMessage(message);

    //         this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
    //           localBasket.products = processedBasketProducts;
    //           this.basket = localBasket;
    //           this.mergeValidationBasket(validatedBasket);

    //           resolve(this.basket);
    //         });
    //         resolve(this.basket);
    //       }).catch((error: Error) => {
    //         // Validation failed but the loyalty was still removed so resolve the basket
    //         this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
    //           localBasket.products = processedBasketProducts;
    //           this.basket = localBasket;
    //           reject(error);
    //         });
    //       })
    //     }).catch((error: Error) => {
    //       reject(error)
    //     });
    //   });          
    // },

    //--------------------------------------------------------------------------------------
    //
    // Basket Promos
    //
    // -------------------------------------------------------------------------------------

    applyPromo(couponcode: string): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return enqueueMutation(BasketMutationPriority.Low, () => new Promise((resolve, reject) => {
        BasketService.applyPromo(this.basket!.id, couponcode).then((localBasket: Basket) => {
          this.validateBasket(false, { skipQueue: true }).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Promo has been applied",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);

              resolve(this.basket);
            });
          }).catch((error: Error) => {
            // Validation failed but the promo was still applied so resolve the basket
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;

              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },
    removePromo(couponcode: string | null): Promise<any> {
      const flashMessageStore = useFlashMessageStore();
      return enqueueMutation(BasketMutationPriority.Low, () => new Promise((resolve, reject) => {
        const reward = this.basket!.appliedrewards![0];
        BasketService.removePromo(this.basket!.id, couponcode).then((localBasket: Basket) => {
          this.validateBasket(false, { skipQueue: true }).then((validatedBasket: Basket) => {
            const message = {
              id: Math.round(Date.now() / 1000),
              messageType: "success",
              text: "Rewards have been removed",
              headline: "",
              delay: 5000, // ms
              icon: "",
              cssClass: "",
            } as FlashMessage;
            flashMessageStore.addMessage(message);

            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              this.mergeValidationBasket(validatedBasket);
              resolve(this.basket);
            });
          }).catch((error: Error) => {
            this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
              localBasket.products = processedBasketProducts;
              this.basket = localBasket;
              reject(error);
            });
          })
        }).catch((error: Error) => {
          reject(error)
        });
      }));          
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Handoff Mode
    //
    // -------------------------------------------------------------------------------------

    applyDelivery(streetaddress: string, city: string, building: string, zipcode: string, specialinstructions: string): Promise<any> {
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        BasketService.applyDelivery(this.basket!.id, streetaddress, city, building, zipcode, specialinstructions).then((localBasket: Basket) => {
          this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
            localBasket.products = processedBasketProducts;
            this.basket = localBasket;

            resolve(this.basket);
          });
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },
    setTip(amount: number): Promise<any> {
      return enqueueMutation(BasketMutationPriority.Medium, () => new Promise((resolve, reject) => {
        BasketService.setTip(this.basket!.id, amount).then((localBasket: Basket) => {
          this.processBasketProducts(localBasket.products, localBasket.vendorid).then((processedBasketProducts) => {
            localBasket.products = processedBasketProducts;
            this.basket = localBasket;

            resolve(this.basket);
          });
        }).catch((error: Error) => {
          reject(error)
        });
      }));
    },

    //--------------------------------------------------------------------------------------
    //
    // Basket Validate and Submit
    //
    // -------------------------------------------------------------------------------------

    validateBasket(silently: boolean, options?: { skipQueue?: boolean }): Promise<any> {
      const runValidation = () => new Promise((resolve, reject) => {
        BasketService.validateBasket(this.basket!.id).then((vBasket: Basket) => {
          this.mergeValidationBasket(vBasket!);
          resolve(this.basket);
        }).catch((error: any) => {
          reject(error);
        })
      });

      if (options?.skipQueue) {
        return runValidation();
      }

      return enqueueMutation(BasketMutationPriority.Low, runValidation);
    },

    submitOrder(order: Order): Promise<any> {
      const cateringOrderStore = useCateringOrderStore();
      order.orderref = this.basket!.id
      return enqueueMutation(BasketMutationPriority.Low, () => new Promise((resolve, reject) => {
        BasketService.submitOrder(this.basket!.id, order).then((order: OrderDetails) => {
          this.processBasketProducts(order.products, (this.basket! as Basket).vendorid).then((processedBasketProducts) => {
            localStorage.removeItem('mypiadaBasketId');
            localStorage.removeItem('cateringOrder');
            localStorage.setItem('orderId', order.id);
            cateringOrderStore.cateringOrder = null as (CateringOrder | null);

            this.basket = null; // Clear out the basket
            order.products = processedBasketProducts;
            this.order = order;
            this.clearBasket({ skipQueue: true }).then(() => {
              resolve(this.order);
            })
          });
        }).catch((error: Error) => {
          reject(error)
        })
      }));
    },
  }
})
